import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { TFunction } from "i18next"

import { Loader2, Mic, MicOff, Volume2 } from "@/components/icons"
import { buttonVariants } from "@/components/ui/button-variants"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { VoiceModeOverlay } from "./voice-mode-overlay"
import { resolveVoiceStageBackground } from "./voice-stage/voice-stage-utils"
import {
  type RealtimeAudioCallbacks,
  type RealtimeAudioError,
  useRealtimeAudio,
} from "../hooks/use-realtime-audio"
import { useVoiceEngineChoice } from "../hooks/use-voice-engine"

/**
 * Play a subtle chime sound using Web Audio API to indicate voice mode is ready.
 * Creates a short, pleasant two-tone chime.
 */
function playReadyChime() {
  try {
    const audioContext = new AudioContext()

    // First tone (higher pitch)
    const osc1 = audioContext.createOscillator()
    const gain1 = audioContext.createGain()
    osc1.connect(gain1)
    gain1.connect(audioContext.destination)
    osc1.frequency.value = 880 // A5
    osc1.type = "sine"
    gain1.gain.setValueAtTime(0.15, audioContext.currentTime)
    gain1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15)
    osc1.start(audioContext.currentTime)
    osc1.stop(audioContext.currentTime + 0.15)

    // Second tone (lower pitch, slightly delayed)
    const osc2 = audioContext.createOscillator()
    const gain2 = audioContext.createGain()
    osc2.connect(gain2)
    gain2.connect(audioContext.destination)
    osc2.frequency.value = 1320 // E6
    osc2.type = "sine"
    gain2.gain.setValueAtTime(0, audioContext.currentTime + 0.08)
    gain2.gain.linearRampToValueAtTime(0.12, audioContext.currentTime + 0.1)
    gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25)
    osc2.start(audioContext.currentTime + 0.08)
    osc2.stop(audioContext.currentTime + 0.25)

    // Clean up after sounds finish
    setTimeout(() => {
      void audioContext.close()
    }, 300)
  } catch {
    // Audio not supported or blocked - fail silently
  }
}

type VoiceButtonVisuals = {
  variant: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
  icon: ReactElement
  tooltip: string
  animationClass?: string
  ringClass?: string
}

const getVoiceButtonVisuals = ({
  isConnecting,
  isUserSpeaking,
  userTranscript,
  isProcessing,
  isAISpeaking,
  assistantTranscript,
  isListening,
  isInVoiceMode,
  t,
}: {
  isConnecting: boolean
  isUserSpeaking: boolean
  userTranscript?: string
  isProcessing: boolean
  isAISpeaking: boolean
  assistantTranscript?: string
  isListening: boolean
  isInVoiceMode: boolean
  t: TFunction
}): VoiceButtonVisuals => {
  if (isConnecting) {
    return {
      variant: "secondary",
      icon: <Loader2 className="size-5 animate-spin" />,
      tooltip: t("voice_mode.connecting"),
    }
  }

  if (isUserSpeaking) {
    return {
      variant: "destructive",
      icon: <Mic className="size-5" />,
      tooltip: userTranscript
        ? t("voice_mode.you_transcript", { text: userTranscript.slice(-40) })
        : t("voice_mode.user_speaking"),
      animationClass: "animate-pulse",
      ringClass: "ring-2 ring-destructive ring-opacity-50",
    }
  }

  if (isProcessing) {
    return {
      variant: "secondary",
      icon: <Loader2 className="size-5 animate-spin" />,
      tooltip: userTranscript
        ? t("voice_mode.processing_transcript", { text: userTranscript.slice(-30) })
        : t("voice_mode.processing"),
    }
  }

  if (isAISpeaking) {
    return {
      variant: "outline",
      icon: <Volume2 className="text-primary size-5" />,
      tooltip: assistantTranscript
        ? t("voice_mode.ai_transcript", { text: assistantTranscript.slice(-40) })
        : t("voice_mode.ai_speaking"),
      ringClass: "ring-2 ring-primary ring-opacity-50 animate-pulse",
    }
  }

  if (isListening) {
    return {
      variant: "default",
      icon: <Mic className="size-5" />,
      tooltip: t("voice_mode.active_hint"),
    }
  }

  if (isInVoiceMode) {
    return {
      variant: "destructive",
      icon: <MicOff className="size-5" />,
      tooltip: t("voice_mode.hang_up"),
    }
  }

  return {
    variant: "ghost",
    icon: <Mic className="size-5" />,
    tooltip: t("voice_mode.start"),
  }
}

export type VoiceInputProps = {
  sessionId?: string
  sessionType?: string
  onUserTranscript?: NonNullable<RealtimeAudioCallbacks["onUserTranscript"]>
  onAssistantTranscriptDelta?: NonNullable<RealtimeAudioCallbacks["onAssistantTranscriptDelta"]>
  onAssistantTranscript?: NonNullable<RealtimeAudioCallbacks["onAssistantTranscriptDone"]>
  onTranscriptionFailure?: NonNullable<RealtimeAudioCallbacks["onTranscriptionFailure"]>
  onToolStart?: (toolName: string) => void
  onToolComplete?: NonNullable<RealtimeAudioCallbacks["onToolComplete"]>
  onInterrupt?: () => void
  onError?: (error: RealtimeAudioError) => void
  /** A structured fair-use refusal frame from the realtime socket. */
  onFairUseRefusal?: (frame: unknown) => void
  onSessionCreated?: (sessionId: string) => void
  onVoiceModeEnd?: () => void
  /** Called to get a function that can send session_context to the WebSocket */
  onGetSendSessionContext?: (sendFn: (sessionId: string) => void) => void
  /** Called to get a function that can toggle voice mode from parent components */
  onGetToggleVoiceMode?: (toggleFn: () => void) => void
  disabled?: boolean
  /** Callback called before voice mode starts - return true to proceed, false to cancel */
  onBeforeVoiceModeStart?: () => boolean
  /** When true, hides the trigger button in the footer (voice mode can still be started programmatically). */
  hideButton?: boolean
}

export function VoiceInput({
  sessionId,
  sessionType,
  onUserTranscript,
  onAssistantTranscriptDelta,
  onAssistantTranscript,
  onTranscriptionFailure,
  onToolStart,
  onToolComplete,
  onInterrupt,
  onError,
  onFairUseRefusal,
  onSessionCreated,
  onVoiceModeEnd,
  onGetSendSessionContext,
  onGetToggleVoiceMode,
  disabled = false,
  onBeforeVoiceModeStart,
  hideButton = false,
}: VoiceInputProps) {
  const { t } = useTranslation()
  const { engine, otherEngine, switchEngine } = useVoiceEngineChoice()
  // Injected into the page once by the server; it cannot change under a mounted app.
  const voiceStageBackground = useMemo(resolveVoiceStageBackground, [])
  const [voiceError, setVoiceError] = useState<RealtimeAudioError | null>(null)

  // Track if we just became ready (for visual pulse animation)
  const [justBecameReady, setJustBecameReady] = useState(false)
  const wasConnectingRef = useRef(false)
  const retryRequestedRef = useRef(false)

  // Memoize callbacks to prevent unnecessary re-renders and effect cleanup cycles
  const callbacks: RealtimeAudioCallbacks = useMemo(
    () => ({
      onUserTranscript,
      onAssistantTranscriptDelta,
      onAssistantTranscriptDone: onAssistantTranscript,
      onTranscriptionFailure,
      onToolStart,
      onToolComplete,
      onInterrupt,
      onError: (error) => {
        setVoiceError(error)
        onError?.(error)
      },
      onFairUseRefusal,
      onVoiceModeStart: () => setVoiceError(null),
      onAutoHangup: (reason) => {
        toast.info(
          reason === "inactivity"
            ? t("voice_mode.inactivity_ended")
            : t("voice_mode.maximum_duration_ended")
        )
      },
      onSessionCreated,
      onVoiceModeEnd,
    }),
    [
      onUserTranscript,
      onAssistantTranscriptDelta,
      onAssistantTranscript,
      onTranscriptionFailure,
      onToolStart,
      onToolComplete,
      onInterrupt,
      onError,
      onFairUseRefusal,
      onSessionCreated,
      onVoiceModeEnd,
      t,
    ]
  )

  const {
    state,
    isInVoiceMode,
    isConnecting,
    isListening,
    isUserSpeaking,
    isAISpeaking,
    isProcessing,
    isPlayingAudio,
    currentTool,
    toolProgress,
    voiceToolApprovals,
    voiceToolApproval,
    isVoiceToolApprovalSubmitting,
    userTranscript,
    assistantTranscript,
    voiceTranscript,
    sessionStartedAt,
    isHangingUp,
    analyserNode,
    toggleVoiceMode: rawToggleVoiceMode,
    startVoiceMode,
    stopVoiceMode,
    sendSessionContext,
    sendVoiceToolDecision,
  } = useRealtimeAudio({ sessionId, sessionType, callbacks, engine: engine ?? "realtime" })

  // Wrapper to check consent before starting voice mode
  const toggleVoiceMode = useCallback(() => {
    // Only check consent when starting voice mode (not when stopping)
    if (!isInVoiceMode && onBeforeVoiceModeStart && !onBeforeVoiceModeStart()) {
      return // Consent check failed or modal shown
    }
    setVoiceError(null)
    rawToggleVoiceMode()
  }, [isInVoiceMode, onBeforeVoiceModeStart, rawToggleVoiceMode])

  const closeVoiceMode = useCallback(() => {
    retryRequestedRef.current = false
    setVoiceError(null)
    stopVoiceMode()
  }, [stopVoiceMode])

  const retryVoiceMode = useCallback(() => {
    setVoiceError(null)
    if (state === "idle") {
      startVoiceMode()
      return
    }

    // Wait for the current socket, microphone, and AudioContext to be fully
    // cleaned up before opening the replacement session.
    retryRequestedRef.current = true
    stopVoiceMode()
  }, [startVoiceMode, state, stopVoiceMode])

  // Bumped to run the restart effect when nothing else changes: switching
  // engines from the error view starts from `idle` and stays there.
  const [restartTick, setRestartTick] = useState(0)

  useEffect(() => {
    if (state !== "idle" || !retryRequestedRef.current) return
    retryRequestedRef.current = false
    startVoiceMode()
  }, [restartTick, startVoiceMode, state])

  // Switching engines mid-call hangs up and reconnects on the other one,
  // through the same cleanup gate a retry uses.
  const switchVoiceEngine = useCallback(() => {
    if (!otherEngine) return
    switchEngine()
    setVoiceError(null)
    retryRequestedRef.current = true
    if (state === "idle") {
      // From the error view there is no call to stop: start one on the other
      // engine once it has been selected.
      setRestartTick((tick) => tick + 1)
      return
    }
    stopVoiceMode()
  }, [otherEngine, state, stopVoiceMode, switchEngine])

  // Expose sendSessionContext to parent so it can send session_id immediately when created
  useEffect(() => {
    if (isInVoiceMode && onGetSendSessionContext) {
      onGetSendSessionContext(sendSessionContext)
    }
  }, [isInVoiceMode, onGetSendSessionContext, sendSessionContext])

  // Expose toggleVoiceMode to parent so it can trigger voice mode from the send button
  useEffect(() => {
    if (onGetToggleVoiceMode) {
      onGetToggleVoiceMode(toggleVoiceMode)
    }
  }, [onGetToggleVoiceMode, toggleVoiceMode])

  // Use ref to avoid cleanup being triggered by stopVoiceMode identity changes
  const stopVoiceModeRef = useRef(stopVoiceMode)
  stopVoiceModeRef.current = stopVoiceMode

  // Detect transition from connecting to listening (ready state)
  useEffect(() => {
    if (wasConnectingRef.current && isListening) {
      // Just became ready - play chime and trigger visual pulse
      playReadyChime()
      setJustBecameReady(true)

      // Clear the pulse animation after it completes
      const timer = setTimeout(() => {
        setJustBecameReady(false)
      }, 600)

      return () => clearTimeout(timer)
    }
    wasConnectingRef.current = isConnecting
  }, [isConnecting, isListening])

  // Reset when voice mode ends
  useEffect(() => {
    if (!isInVoiceMode) {
      wasConnectingRef.current = false
      setJustBecameReady(false)
    }
  }, [isInVoiceMode])

  // Auto-disconnect on unmount only
  useEffect(() => {
    return () => {
      stopVoiceModeRef.current()
    }
  }, []) // Empty deps - only run cleanup on actual unmount

  const { variant, icon, tooltip, animationClass, ringClass } = getVoiceButtonVisuals({
    isConnecting,
    isUserSpeaking,
    userTranscript,
    isProcessing,
    isAISpeaking,
    assistantTranscript,
    isListening,
    isInVoiceMode,
    t,
  })

  // Add ready pulse class if we just became ready
  const readyPulseClass = justBecameReady ? "animate-ready-pulse" : ""

  // Show overlay when in voice mode (not during initial connection)
  // The window goes the moment the caller hangs up; the Live route may still
  // be closing and saving behind it (the hook ends the session on its frame).
  const showOverlay = ((isInVoiceMode || isConnecting) && !isHangingUp) || voiceError !== null
  // No resolved engine means no voice surface at all. Hide the trigger but keep
  // the hook and its effects mounted, so the parent still receives the toggle
  // and session-context handles it registers on every render.
  const showVoiceButton = !hideButton && engine !== null

  return (
    <>
      {showVoiceButton && (
        <Tooltip>
          <TooltipTrigger
            aria-label={tooltip}
            disabled={disabled}
            type="button"
            className={cn(
              buttonVariants({
                size: "icon",
                variant: variant,
                className: cn(
                  "size-11 cursor-pointer transition-[color,background-color,border-color,box-shadow,opacity,transform] duration-300",
                  animationClass,
                  ringClass,
                  readyPulseClass,
                  disabled && "cursor-not-allowed opacity-50"
                ),
              })
            )}
            onClick={() => {
              if (!disabled) toggleVoiceMode()
            }}
            onMouseDown={(e) => {
              e.preventDefault()
            }}
          >
            {icon}
          </TooltipTrigger>
          <TooltipContent className="max-w-[300px]" side="bottom" sideOffset={5}>
            <p>{tooltip}</p>
            {isInVoiceMode && !isConnecting && (
              <p className="text-muted-foreground mt-1 text-xs">{t("voice_mode.tooltip_hint")}</p>
            )}
          </TooltipContent>
        </Tooltip>
      )}

      {/* Voice Mode Overlay */}
      <VoiceModeOverlay
        analyserNode={analyserNode}
        background={voiceStageBackground}
        currentTool={currentTool}
        engine={engine}
        error={voiceError}
        isPlayingAudio={isPlayingAudio}
        isVoiceToolApprovalSubmitting={isVoiceToolApprovalSubmitting}
        open={showOverlay}
        sessionStartedAt={sessionStartedAt}
        state={state}
        toolProgress={toolProgress}
        transcript={voiceTranscript}
        voiceToolApproval={voiceToolApproval}
        voiceToolApprovalCount={voiceToolApprovals.length}
        onClose={closeVoiceMode}
        onRetry={retryVoiceMode}
        onSwitchEngine={otherEngine ? switchVoiceEngine : undefined}
        onVoiceToolApprovalDecision={sendVoiceToolDecision}
      />
    </>
  )
}
